import React from 'react';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const integrations = [
  { name: 'n8n', description: 'Workflow automation for connecting services', category: 'automation', logo: '🔄' },
  { name: 'Make', description: 'Visual platform to design and automate workflows', category: 'automation', logo: '⚡' },
  { name: 'Zapier', description: 'Connect apps and automate workflows', category: 'automation', logo: '⚡' },
  { name: 'Go High Level', description: 'All-in-one marketing platform integration', category: 'crm', logo: '📈' },
  { name: 'Cal.com', description: 'Scheduling infrastructure for calendar integration', category: 'scheduling', logo: '📅' },
  { name: 'Google Calendar', description: 'Sync for automated scheduling and booking', category: 'scheduling', logo: '📆' },
  { name: 'Salesforce', description: 'CRM integration for customer data and sales', category: 'crm', logo: '☁️' },
  { name: 'HubSpot', description: 'CRM integration for customer data management', category: 'crm', logo: '🧡' },
  { name: 'OpenAI', description: 'Connect to GPT-4o, GPT-4o-mini for AI capabilities', category: 'ai', logo: '🤖' },
  { name: 'Anthropic', description: 'Leverage Claude 3.5 Sonnet for AI capabilities', category: 'ai', logo: '🧠' },
  { name: 'Google Gemini', description: 'Leverage Gemini 2.0 Flash for AI capabilities', category: 'ai', logo: '✨' },
  { name: 'Groq', description: 'Ultra-fast inference for real-time applications', category: 'ai', logo: '⚡' },
  { name: 'Slack', description: 'Team communication and collaboration', category: 'communication', logo: '💬' },
  { name: 'Twilio', description: 'SMS, voice, and communication APIs', category: 'communication', logo: '📱' },
  { name: 'Square', description: 'Payment processing and point-of-sale integration', category: 'payments', logo: '💳' },
  { name: 'Stripe', description: 'Online payment processing infrastructure', category: 'payments', logo: '💵' },
  { name: 'Mailchimp', description: 'Email marketing platform', category: 'marketing', logo: '📧' },
  { name: 'WebHook', description: 'Add your own Custom WebHook integration', category: 'custom', logo: '🔗' },
  { name: 'Web Search', description: 'Enable Web Search for more context', category: 'ai', logo: '🔍' },
  { name: 'Trello', description: 'Trello integration for project management', category: 'productivity', logo: '📋' },
  { name: 'Airtable', description: 'Airtable Database integration', category: 'productivity', logo: '📊' },
  { name: 'Google Sheets', description: 'Google Sheet integration for data storage', category: 'productivity', logo: '📑' },
  { name: 'Zoho', description: 'Zoho integration for CRM and sales management', category: 'crm', logo: '📘' },
  { name: 'Custom API', description: 'Custom API integration for your own services', category: 'custom', logo: '🔧' },
];

const categories = [
  { id: 'all', name: 'All Integrations' },
  { id: 'ai', name: 'AI Models' },
  { id: 'crm', name: 'CRM' },
  { id: 'automation', name: 'Automation' },
  { id: 'scheduling', name: 'Scheduling' },
  { id: 'communication', name: 'Communication' },
  { id: 'payments', name: 'Payments' },
  { id: 'productivity', name: 'Productivity' },
  { id: 'custom', name: 'Custom' },
];

const IntegrationsPage = () => {
  const [activeCategory, setActiveCategory] = React.useState('all');

  const filteredIntegrations = activeCategory === 'all' 
    ? integrations 
    : integrations.filter(i => i.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero */}
        <div className="max-w-7xl mx-auto px-6 text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Connect With Your<br /><span className="text-teal-400">Tech Stack</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Connect your voice AI assistants with the tools and platforms you already use. 
            From AI models to CRM systems, automation tools to communication platforms.
          </p>
        </div>

        {/* Category Filter */}
        <div className="max-w-7xl mx-auto px-6 mb-12">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === cat.id
                    ? 'bg-teal-400 text-black'
                    : 'bg-[#1a1a1f] text-gray-300 hover:bg-[#252530]'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Integrations Grid */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredIntegrations.map((integration, index) => (
              <div
                key={index}
                className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-all hover:scale-105"
              >
                <div className="text-4xl mb-4">{integration.logo}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{integration.name}</h3>
                <p className="text-gray-500 text-sm">{integration.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto px-6 mt-20">
          <div className="bg-gradient-to-r from-teal-500/20 to-blue-500/20 border border-teal-500/30 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              Need a Custom Integration?
            </h2>
            <p className="text-gray-400 mb-6">
              We can build custom integrations for your specific needs. Contact us to discuss your requirements.
            </p>
            <a
              href="/contact-us"
              className="inline-flex items-center gap-2 bg-teal-400 hover:bg-teal-500 text-black px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Contact Us
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default IntegrationsPage;
